import React, { useState } from 'react'
import {
  DocumentIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  TrashIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { Document, DocumentStatus, DocumentType } from '../types'
import { useDeleteDocument, useDownloadDocument } from '../hooks/useDocuments'
import { formatDate, formatFileSize } from '../utils/formatters'
import LoadingSpinner from './LoadingSpinner'

interface DocumentListProps {
  documents: Document[]
  showActions?: boolean
  onDocumentUpdated?: () => void
}

const documentTypeLabels: Record<DocumentType, string> = {
  [DocumentType.MEDICAL_CERTIFICATE]: 'Medical Certificate',
  [DocumentType.BURIAL_PERMIT]: 'Burial Permit',
  [DocumentType.IDENTIFICATION]: 'ID Document',
  [DocumentType.CONSENT_FORM]: 'Consent Form',
  [DocumentType.OTHER]: 'Other',
}

const statusConfig: Record<DocumentStatus, { label: string; color: string; icon: React.ComponentType<any> }> = {
  [DocumentStatus.PENDING]: {
    label: 'Pending Review',
    color: 'bg-yellow-100 text-yellow-800',
    icon: ClockIcon,
  },
  [DocumentStatus.UPLOADED]: {
    label: 'Uploaded',
    color: 'bg-blue-100 text-blue-800',
    icon: DocumentIcon,
  },
  [DocumentStatus.VERIFIED]: {
    label: 'Verified',
    color: 'bg-green-100 text-green-800',
    icon: CheckCircleIcon,
  },
  [DocumentStatus.REJECTED]: {
    label: 'Rejected',
    color: 'bg-red-100 text-red-800',
    icon: XCircleIcon,
  },
}

function DocumentList({ documents, showActions = true, onDocumentUpdated }: DocumentListProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null)
  const deleteMutation = useDeleteDocument()
  const downloadMutation = useDownloadDocument()

  const handleDownload = async (document: Document) => {
    try {
      await downloadMutation.mutateAsync({
        id: document.id,
        filename: document.originalName,
      })
    } catch (error) {
      // Error is handled by the mutation hook
    }
  }

  const handleDelete = async (documentId: string) => {
    try {
      await deleteMutation.mutateAsync(documentId)
      setShowDeleteConfirm(null)
      onDocumentUpdated?.()
    } catch (error) {
      // Error is handled by the mutation hook
    }
  }

  const getStatusBadge = (status: DocumentStatus) => {
    const config = statusConfig[status]
    const Icon = config.icon

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    )
  }

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) {
      return '📄'
    } else if (mimeType.includes('word') || mimeType.includes('document')) {
      return '📝'
    } else if (mimeType.includes('image')) {
      return '🖼️'
    }
    return '📎'
  }

  if (documents.length === 0) {
    return (
      <div className="text-center py-12">
        <DocumentIcon className="mx-auto h-12 w-12 text-neutral-400" />
        <h3 className="mt-2 text-sm font-medium text-neutral-900">No documents uploaded</h3>
        <p className="mt-1 text-sm text-neutral-500">
          Upload documents to get started with the funeral arrangements.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {documents.map((document) => (
        <div key={document.id} className="card">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div className="flex items-start flex-1">
                <div className="text-2xl mr-3">
                  {getFileIcon(document.mimeType)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-medium text-neutral-900 truncate">
                      {document.type === DocumentType.OTHER
                        ? document.originalName
                        : documentTypeLabels[document.type]}
                    </h4>
                    {getStatusBadge(document.status)}
                  </div>

                  {document.type === DocumentType.OTHER && (
                    <p className="text-xs text-neutral-500 mb-2">{document.originalName}</p>
                  )}

                  <div className="flex items-center text-xs text-neutral-500 space-x-4">
                    <span>{formatFileSize(document.fileSize)}</span>
                    <span>Uploaded {formatDate(document.uploadedAt)}</span>
                    {document.verifiedAt && (
                      <span>Verified {formatDate(document.verifiedAt)}</span>
                    )}
                  </div>

                  {document.verificationNotes && (
                    <div className="mt-2 p-2 bg-neutral-50 rounded text-xs text-neutral-600">
                      <strong>Notes:</strong> {document.verificationNotes}
                    </div>
                  )}
                </div>
              </div>

              {showActions && (
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleDownload(document)}
                    disabled={downloadMutation.isPending}
                    className="p-2 text-neutral-400 hover:text-neutral-600 disabled:opacity-50"
                    title="Download document"
                  >
                    {downloadMutation.isPending ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    )}
                  </button>

                  {document.status !== DocumentStatus.VERIFIED && (
                    <button
                      onClick={() => setShowDeleteConfirm(document.id)}
                      disabled={deleteMutation.isPending}
                      className="p-2 text-red-400 hover:text-red-600 disabled:opacity-50"
                      title="Delete document"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-neutral-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-neutral-900">
                      Delete Document
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-neutral-500">
                        Are you sure you want to delete this document? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-neutral-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => handleDelete(showDeleteConfirm)}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-neutral-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowDeleteConfirm(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DocumentList